import { Paragraph2, Title1 } from "@bounce/design-system"
import type { Nullable } from "@bounce/util"

import { HeroContainer } from "./HeroContainer"
import type { ImgixParams } from "../../../helpers/image"
import { Dropdown } from "../../Navigation/Dropdown/Dropdown"
import { DropdownItem } from "../../Navigation/Dropdown/DropdownItem"
import type { PNImageProps } from "../PNImage/PNImage"

export type HeroWithDropdownProps = {
  eyebrow?: Nullable<string>
  title?: Nullable<string>
  subtitle?: Nullable<string>
  src: PNImageProps["src"]
  imageSize?: "contain" | "cover"
  imgixParams?: ImgixParams
  dropdownLabel?: Nullable<string>
  /**
   * Format: "Label|path, Label|path, Label|path"
   * Example: "New York|/new-york, London|/london, Paris|/paris"
   */
  dropdownOptions?: Nullable<string>
  hideImageOnMobile?: boolean
}

type DropdownOption = {
  label: string
  href: string
}

export const HeroWithDropdown = ({
  dropdownLabel,
  dropdownOptions,
  eyebrow,
  hideImageOnMobile = false,
  imageSize = "cover",
  imgixParams,
  src,
  subtitle,
  title,
}: HeroWithDropdownProps) => {
  const className = imageSize === "contain" ? "!min-h-fit" : undefined
  const imageClassName = imageSize === "contain" ? "!object-contain md:!w-1/3 lg:!w-1/2" : undefined
  const hasDropdown = !!dropdownLabel && !!dropdownOptions

  // Parse dropdown options format: "Label|path, Label|path, Label|path"
  const options: DropdownOption[] = dropdownOptions
    ? dropdownOptions.split(",").map(option => {
        const parts = option.trim().split("|")
        return {
          label: parts[0]?.trim() || "",
          href: parts.length > 1 ? parts[1]?.trim() || "#" : "#",
        }
      })
    : []

  return (
    <HeroContainer
      image={src}
      hideImageOnMobile={hideImageOnMobile}
      imgixParams={imgixParams}
      className={className}
      imageClassName={imageClassName}>
      {!!eyebrow && <Paragraph2 className="mb-2 flex text-neutral-800">{eyebrow}</Paragraph2>}
      {!!title && <Title1 className="mb-5 break-words text-black md:mb-6">{title}</Title1>}
      {!!subtitle && <p className="text-base text-black md:text-xl xl:text-2xl">{subtitle}</p>}
      {hasDropdown && (
        <div className="mt-10 flex w-full">
          <Dropdown id="hero-dropdown" label={dropdownLabel || "Select an option"}>
            {options.map((option, index) => (
              <DropdownItem key={index} href={option.href}>
                {option.label}
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      )}
    </HeroContainer>
  )
}
